import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

interface DropdownItem {
  label: string;
  link: string;
}

interface CityGroup {
  region: string;
  cities: DropdownItem[];
}

const serviceItems: DropdownItem[] = [
  { label: 'Compliance & Eligibility Review', link: '/compliance-review' },
  { label: 'Short-Term Rental Co-Hosting', link: '/str-cohosting' },
  { label: 'Medium-Term Furnished Rentals', link: '/medium-term-rentals' },
  { label: 'Listing & Setup Support', link: '/listing-setup' },
];

const cityGroups: CityGroup[] = [
  {
    region: 'British Columbia',
    cities: [
      { label: 'Vancouver', link: '/vancouver-short-term-rental-rules' },
      { label: 'North Vancouver', link: '/north-vancouver-str-bylaws' },
      { label: 'Burnaby', link: '/burnaby-str-regulations' },
      { label: 'Surrey', link: '/surrey-rental-regulations' },
      { label: 'Langley', link: '/langley-str-licensing' },
      { label: 'Victoria', link: '/victoria-str-regulations' },
      { label: 'Whistler', link: '/whistler-str-regulations' },
    ],
  },
  {
    region: 'Alberta',
    cities: [
      { label: 'Calgary & Edmonton', link: '/alberta-short-term-rental-regulations' },
    ],
  },
  {
    region: 'Ontario',
    cities: [
      { label: 'Toronto', link: '/toronto-str-regulations' },
    ],
  },
];

function DesktopDropdown({
  label,
  children,
  isOpen,
  onOpen,
  onClose,
}: {
  label: string;
  children: React.ReactNode;
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
}) {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleEnter = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    onOpen();
  };

  const handleLeave = () => {
    timeoutRef.current = setTimeout(() => {
      onClose();
    }, 150);
  };

  return (
    <div
      className="relative"
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
    >
      <button
        className="text-base font-medium transition-colors cursor-pointer whitespace-nowrap text-[#505A5A] hover:text-[#882629] flex items-center gap-1"
      >
        {label}
        <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}></i>
      </button>
      <div
        className={`absolute top-full left-1/2 -translate-x-1/2 pt-3 transition-all duration-200 ${
          isOpen ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2'
        }`}
      >
        {children}
      </div>
    </div>
  );
}

function MobileDropdown({
  label,
  children,
  isOpen,
  onToggle,
}: {
  label: string;
  children: React.ReactNode;
  isOpen: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="relative">
      <button
        onClick={onToggle}
        className="text-sm font-medium transition-colors cursor-pointer whitespace-nowrap text-[#505A5A] hover:text-[#882629] flex items-center gap-0.5"
      >
        {label}
        <i className={`ri-arrow-down-s-line text-xs transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}></i>
      </button>
      <div
        className={`absolute top-full left-0 pt-2 z-50 transition-all duration-200 ${
          isOpen ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2'
        }`}
      >
        {children}
      </div>
    </div>
  );
}

export function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [openDesktop, setOpenDesktop] = useState<string | null>(null);
  const [openMobile, setOpenMobile] = useState<string | null>(null);
  const [expandedRegion, setExpandedRegion] = useState<string | null>(null);
  const [mobileExpandedRegion, setMobileExpandedRegion] = useState<string | null>(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    setOpenDesktop(null);
    setOpenMobile(null);
    setExpandedRegion(null);
    setMobileExpandedRegion(null);
  }, [location.pathname]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    // If we're on the homepage, scroll directly
    if (location.pathname === '/') {
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      // If we're on another page, navigate to homepage with hash
      navigate(`/#${id}`);
    }
  };

  // Handle hash navigation on mount and location change
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace('#', '');
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  }, [location]);

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 w-full ${
          isScrolled ? 'bg-white shadow-md' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-6 flex items-center justify-between w-full">
          <div className="flex items-center flex-shrink-0">
            <Link to="/" className="cursor-pointer">
              <img
                src="https://static.readdy.ai/image/55893c4a5915be14917a0a81b6ec721d/f5868736a4ed9c94ff4146ae0bef5b91.png"
                alt="Furnished Rental Management Canada Logo"
                className="h-20 w-auto drop-shadow-sm"
              />
            </Link>
          </div>

          {/* Mobile: Horizontal scrollable tabs */}
          <div className="flex md:hidden items-center overflow-x-auto overflow-y-hidden scrollbar-hide ml-4 -mr-6 pr-6" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', touchAction: 'pan-x', WebkitOverflowScrolling: 'touch' }}>
            <div className="flex items-center space-x-6 whitespace-nowrap">
              <MobileDropdown
                label="Services"
                isOpen={openMobile === 'services'}
                onToggle={() => setOpenMobile(openMobile === 'services' ? null : 'services')}
              >
                <div className="bg-white rounded-xl shadow-xl border border-gray-100 py-2 min-w-[220px]">
                  {serviceItems.map((item) => (
                    <Link
                      key={item.link}
                      to={item.link}
                      className="block px-4 py-2.5 text-sm text-[#505A5A] hover:bg-[#F9F5F0] hover:text-[#882629] transition-colors cursor-pointer"
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              </MobileDropdown>
              <button
                onClick={() => { setOpenMobile(null); scrollToSection('how-it-works'); }}
                className="text-sm font-medium transition-colors cursor-pointer whitespace-nowrap text-[#505A5A] hover:text-[#882629]"
              >
                How It Works
              </button>
              <MobileDropdown
                label="Cities"
                isOpen={openMobile === 'cities'}
                onToggle={() => setOpenMobile(openMobile === 'cities' ? null : 'cities')}
              >
                <div className="bg-white rounded-xl shadow-xl border border-gray-100 py-2 min-w-[220px]">
                  {cityGroups.map((group) => (
                    <div key={group.region}>
                      <button
                        onClick={() => setMobileExpandedRegion(mobileExpandedRegion === group.region ? null : group.region)}
                        className="w-full flex items-center justify-between px-4 py-2.5 text-sm font-semibold text-[#505A5A] hover:bg-[#F9F5F0] cursor-pointer"
                      >
                        {group.region}
                        <i className={`ri-arrow-right-s-line text-xs transition-transform duration-200 ${mobileExpandedRegion === group.region ? 'rotate-90' : ''}`}></i>
                      </button>
                      <div className={`overflow-hidden transition-all duration-200 ${mobileExpandedRegion === group.region ? 'max-h-96' : 'max-h-0'}`}>
                        {group.cities.map((city) => (
                          <Link
                            key={city.link}
                            to={city.link}
                            className="block pl-8 pr-4 py-2 text-sm text-[#505A5A]/80 hover:bg-[#F9F5F0] hover:text-[#882629] transition-colors cursor-pointer"
                          >
                            {city.label}
                          </Link>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </MobileDropdown>
              <Link
                to="/buildings-that-allow-str"
                className="text-sm font-medium transition-colors cursor-pointer whitespace-nowrap text-[#505A5A] hover:text-[#882629]"
              >
                STR Buildings
              </Link>
              <button
                onClick={() => { setOpenMobile(null); scrollToSection('pricing'); }}
                className="text-sm font-medium transition-colors cursor-pointer whitespace-nowrap text-[#505A5A] hover:text-[#882629]"
              >
                Pricing
              </button>
              <Link
                to="/contact"
                className="bg-[#882629] text-white px-6 py-2 rounded-full text-sm font-semibold hover:bg-[#6E1F22] transition-colors cursor-pointer whitespace-nowrap shadow-md"
              >
                Contact
              </Link>
            </div>
          </div>

          {/* Desktop: Normal layout */}
          <div className="hidden md:flex items-center space-x-12">
            <DesktopDropdown
              label="Services"
              isOpen={openDesktop === 'services'}
              onOpen={() => setOpenDesktop('services')}
              onClose={() => setOpenDesktop(null)}
            >
              <div className="bg-white rounded-xl shadow-xl border border-gray-100 py-2 min-w-[280px]">
                {serviceItems.map((item) => (
                  <Link
                    key={item.link}
                    to={item.link}
                    className="block px-5 py-3 text-sm text-[#505A5A] hover:bg-[#F9F5F0] hover:text-[#882629] transition-colors cursor-pointer"
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            </DesktopDropdown>
            <button
              onClick={() => scrollToSection('how-it-works')}
              className="text-base font-medium transition-colors cursor-pointer whitespace-nowrap text-[#505A5A] hover:text-[#882629]"
            >
              How It Works
            </button>
            <DesktopDropdown
              label="Cities"
              isOpen={openDesktop === 'cities'}
              onOpen={() => setOpenDesktop('cities')}
              onClose={() => setOpenDesktop(null)}
            >
              <div className="bg-white rounded-xl shadow-xl border border-gray-100 py-3 min-w-[260px]">
                {cityGroups.map((group, idx) => (
                  <div key={group.region}>
                    {idx > 0 && <div className="border-t border-gray-100 my-1"></div>}
                    <button
                      onClick={() => setExpandedRegion(expandedRegion === group.region ? null : group.region)}
                      className="w-full flex items-center justify-between px-5 py-2.5 text-sm font-semibold text-[#505A5A] hover:bg-[#F9F5F0] cursor-pointer transition-colors"
                    >
                      <span>{group.region}</span>
                      <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${expandedRegion === group.region ? 'rotate-180' : ''}`}></i>
                    </button>
                    <div className={`overflow-hidden transition-all duration-200 ${expandedRegion === group.region ? 'max-h-96' : 'max-h-0'}`}>
                      {group.cities.map((city) => (
                        <Link
                          key={city.link}
                          to={city.link}
                          className="block pl-9 pr-5 py-2.5 text-sm text-[#505A5A]/80 hover:bg-[#F9F5F0] hover:text-[#882629] transition-colors cursor-pointer"
                        >
                          {city.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </DesktopDropdown>
            <Link
              to="/buildings-that-allow-str"
              className="text-base font-medium transition-colors cursor-pointer whitespace-nowrap text-[#505A5A] hover:text-[#882629]"
            >
              STR Buildings
            </Link>
            <button
              onClick={() => scrollToSection('pricing')}
              className="text-base font-medium transition-colors cursor-pointer whitespace-nowrap text-[#505A5A] hover:text-[#882629]"
            >
              Pricing
            </button>
            <Link
              to="/contact"
              className="bg-[#882629] text-white px-8 py-3 rounded-full text-base font-semibold hover:bg-[#6E1F22] transition-colors cursor-pointer whitespace-nowrap shadow-md"
            >
              Contact
            </Link>
          </div>
        </div>
      </nav>
    </>
  );
}
